import {
	AssetWithRawElements,
	ResourceContainerOrAsset,
	VideoLinkOrExtLinkOrAssetType,
} from '@/types'
import { ElementModels } from '@kontent-ai/delivery-sdk'
import { AssetModels, TaxonomyModels } from '@kontent-ai/management-sdk'
import format from 'date-fns/format'
import {
	getStageTagsByTaxoTerms,
	getTaxoCodenamesFromTaxoTerms,
	setTaxonomiesForAssets,
} from '..'
import {
	isResourceContainer,
	isWebLinkTeachingadviceExtended,
	isWebLinkVideo,
	isWebLinkVideoOrExtOrTeachingAdviceExtended,
	isWebLinkext,
} from '../type_predicates'

export const getVideoLinkOrExtLinkOrAssetHeadline = (obj) => {
	return (
		(isWebLinkVideoOrExtOrTeachingAdviceExtended(obj) ||
		isResourceContainer(obj)
			? obj.elements?.title?.value
			: obj.title || obj.fileName) || ''
	).trim()
}

export const getVideoLinkOrExtLinkOrAssetSyllabusLabels = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	const syllabuses =
		isWebLinkVideoOrExtOrTeachingAdviceExtended(obj) ||
		isResourceContainer(obj)
			? obj.elements?.syllabus?.value || []
			: obj.syllabus
	return syllabuses.length > 1 ? 'Multiple syllabuses' : syllabuses?.[0]?.name
}

export const getVideoLinkOrExtLinkOrAssetSyllabusTaxoCodenames = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	const syllabus =
		isWebLinkVideoOrExtOrTeachingAdviceExtended(obj) ||
		isResourceContainer(obj)
			? obj.elements?.syllabus?.value || []
			: obj.syllabus
	return getTaxoCodenamesFromTaxoTerms(syllabus)
}
export const getVideoLinkOrExtLinkOrAssetStageTaxoCodenames = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	const taxos =
		isWebLinkVideoOrExtOrTeachingAdviceExtended(obj) ||
		isResourceContainer(obj)
			? obj.elements?.stages__stages?.value || []
			: obj.stage
	return getTaxoCodenamesFromTaxoTerms(taxos)
}
export const getVideoLinkOrExtLinkOrAssetStageYearTaxoCodenames = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	const taxos =
		isWebLinkVideoOrExtOrTeachingAdviceExtended(obj) ||
		isResourceContainer(obj)
			? obj.elements?.stages__stage_years?.value || []
			: obj.stage_year
	return getTaxoCodenamesFromTaxoTerms(taxos)
}
export const getVideoLinkOrExtLinkOrAssetResourceTypeTaxoCodenames = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	if (isWebLinkTeachingadviceExtended(obj)) return []
	const taxos =
		isWebLinkVideo(obj) || isWebLinkext(obj) || isResourceContainer(obj)
			? obj.elements?.resource_type?.value || []
			: obj.resource_type
	return getTaxoCodenamesFromTaxoTerms(taxos)
}

export const getVideoLinkOrExtLinkOrAssetStageTags = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	const stages =
		isWebLinkVideoOrExtOrTeachingAdviceExtended(obj) ||
		isResourceContainer(obj)
			? obj.elements?.stages__stages?.value || []
			: obj.stage
	return getStageTagsByTaxoTerms(stages)
}

export const getVideoLinkOrExtLinkOrAssetLastModified = (
	obj: VideoLinkOrExtLinkOrAssetType,
): string => {
	let lastModified: string = ''

	if (isWebLinkVideo(obj)) {
		lastModified =
			obj.elements?.date?.value || obj.system?.lastModified || ''
	} else if (isWebLinkTeachingadviceExtended(obj) || isWebLinkext(obj)) {
		lastModified =
			obj.elements?.updated?.value || obj.system?.lastModified || ''
	} else if (isResourceContainer(obj)) {
		lastModified =
			obj.elements?.asset_published?.value ||
			obj.system?.lastModified ||
			''
	} else {
		if (obj.assetpublishedmonth?.length && obj.assetpublishedyear?.length) {
			const day = parseInt(
				obj.assetpublishedday?.length
					? obj.assetpublishedday[0].name
					: '1',
			)
			return new Date(
				parseInt(obj.assetpublishedyear[0].name),
				parseInt(obj.assetpublishedmonth[0].name) - 1,
				day,
			).toISOString()
		}
		lastModified = (obj.lastModified?.toString() as string) || ''
	}
	return lastModified
}

export const getVideoLinkOrExtLinkOrAssetUrl = (
	obj: VideoLinkOrExtLinkOrAssetType,
) => {
	let url = ''
	if (isWebLinkVideo(obj)) {
		url = obj.elements?.video_url?.value || ''
	} else if (isWebLinkext(obj)) {
		url = obj.elements?.link_url?.value || ''
	} else if (isWebLinkTeachingadviceExtended(obj)) {
		url = obj.link || ''
	} else if (isResourceContainer(obj)) {
		url = `/file/${obj.system?.id || ''}`
	} else {
		url = obj.url || ''
	}
	return url
}
export const matchFilesWithResourceAssets = (
	file: ResourceContainerOrAsset,
	resource: ElementModels.AssetModel,
) => {
	let fileUrl = ''
	let base = ''
	if (isResourceContainer(file)) {
		fileUrl = `/file/${file.system.id}`
		base = 'https://xxx.com'
	} else {
		fileUrl = file.url
	}

	const urlFile = new URL(fileUrl, base)
	const urlResource = new URL(resource.url)
	return urlFile.pathname === urlResource.pathname
}

export const getAllAssetsWithTaxo = (
	assetItems: AssetModels.Asset[],
	taxonomyItems: TaxonomyModels.Taxonomy[],
) => {
	const assetsWithTaxonomies = setTaxonomiesForAssets(
		assetItems,
		taxonomyItems,
	)
	return (
		assetsWithTaxonomies?.map((item) => {
			const {
				fileReference,
				imageHeight,
				imageWidth,
				folder,
				_raw,
				...rest
			} = item
			return rest as AssetWithRawElements
		}) || []
	)
}

export const getAssetUpdatedDate = (asset: AssetWithRawElements) => {
	const lastModified = getVideoLinkOrExtLinkOrAssetLastModified(asset)
	return format(new Date(lastModified), 'MMM yyyy')
}
